import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useInputs } from '@pages/MapCreateOrEdit/components/AddPointGroupDialog/hooks/useInputs'
import { PageContext } from '@pages/MapCreateOrEdit/MapCreateOrEdit'
import { PointGroupFormValues } from '@pages/MapCreateOrEdit/types'

import { useFetchAllObjectQuery, useFetchObjectByCodeNoCacheMutation } from '@redux/api'

import { MODAL_TYPE } from '@constants'
import { AutocompleteOption } from '@types'

import { getDefaultValues } from '../helpers'

import { useHandlers } from './useHandlers'

export const useAddPointGroupDialog = () => {
  const { addPointGroupDialogData } = useContext(PageContext)

  const { data: objects } = useFetchAllObjectQuery()
  const [fetchObjectByCode, { data: selectedObjectData }] = useFetchObjectByCodeNoCacheMutation({})

  const [isShowPointColorValueScriptDialog, setIsShowPointColorValueScriptDialog] = useState(false)
  const [isShowLabelValueScriptDialog, setIsShowLabelValueScriptDialog] = useState(false)

  const methods = useForm<PointGroupFormValues>({ defaultValues: getDefaultValues() })

  const {
    reset,
    watch,
    formState: { isDirty },
  } = methods

  const watchObjectCode = watch('objectCode')
  const watchGetDataCommand = watch('getDataCommand')
  const watchLabelValue = watch('label.value')
  const watchLabelBindType = watch('label.bindType')
  const watchGetDataCommandName = watch('getDataCommand.name')
  const watchPointColorValue = watch('pointStyle.color.value')
  const watchPointColorBindType = watch('pointStyle.color.bindType')

  const objectsOptions: AutocompleteOption[] = useMemo(
    () => objects?.map(obj => ({ id: obj.code, label: obj.title })) || [],
    [objects]
  )

  const selectedObjectFields = useMemo(() => {
    if (!watchObjectCode || !selectedObjectData?.fields?.length) {
      return []
    }

    return selectedObjectData.fields
  }, [selectedObjectData])

  const selectedObjectFieldsOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectCode || !selectedObjectData?.fields?.length) {
      return []
    }

    return selectedObjectData.fields.map(field => ({ id: field.name, label: field.name })) || []
  }, [objects, selectedObjectData])

  const selectedObjectCommandsOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectCode || !selectedObjectData?.commands?.length) {
      return []
    }

    return (
      selectedObjectData.commands.map(field => ({
        id: field.code,
        label: field.code,
        httpMethod: field.httpMethod,
      })) || []
    )
  }, [watchObjectCode, selectedObjectData])

  useEffect(() => {
    if (!addPointGroupDialogData) {
      return
    }

    if (addPointGroupDialogData.modalType === MODAL_TYPE.CREATE) {
      reset(getDefaultValues())

      return
    }

    if (
      addPointGroupDialogData.modalType === MODAL_TYPE.EDIT &&
      addPointGroupDialogData.pointGroupData
    ) {
      reset(addPointGroupDialogData.pointGroupData)
    }
  }, [])

  const handlers = useHandlers({
    methods,
    setIsShowPointColorValueScriptDialog,
    setIsShowLabelValueScriptDialog,
  })

  const inputs = useInputs({
    objectsOptions,
    selectedObjectFieldsOptions,
    selectedObjectCommandsOptions,
    handleChangeGetDataCommand: handlers.handleChangeGetDataCommand,
    watchObjectCode,
    handleChangePointColorBindType: handlers.handleChangePointColorBindType,
  })

  useEffect(() => {
    if (watchObjectCode?.id) {
      fetchObjectByCode(watchObjectCode.id)
    }
  }, [watchObjectCode])

  return {
    state: {
      methods,
      isDirty,
      watchObjectCode,
      watchGetDataCommand,
      watchLabelValue,
      watchLabelBindType,
      watchGetDataCommandName,
      watchPointColorBindType,
      watchPointColorValue,
      isShowPointColorValueScriptDialog,
      isShowLabelValueScriptDialog,
    },
    inputs,
    handlers,
    data: {
      selectedObjectFields,
    },
  }
}
